import { NavLink, Outlet } from "react-router-dom";
import { Briefcase, ClipboardList, Inbox, Lock, LogOut, Map, Tag } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { SidebarShell } from "./SidebarShell";
import { CompanyAnnouncement } from "./CompanyAnnouncement";
import { PlatformDashboardAlert } from "./PlatformDashboardAlert";

const MyJobsLink = ({ className }) => (
  <NavLink
    to="/jobs"
    end
    data-testid="locksmith-nav-my-jobs"
    className={({ isActive }) =>
      `${className} ${isActive ? "bg-blue-900 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
    }
  >
    <Briefcase className="h-4 w-4" /> Warrants
  </NavLink>
);

const PartnersLink = ({ className }) => (
  <NavLink to="/jobs/partners" aria-label="Partners" title="Partners" data-testid="worker-nav-partners"
    className={({ isActive }) => `${className} ${isActive ? "bg-blue-900 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
    <Tag className="h-4 w-4" /> <span className="hidden sm:inline">Partners</span>
  </NavLink>
);

const AvailableLink = ({ className }) => (
  <NavLink
    to="/jobs/available"
    data-testid="locksmith-nav-available"
    className={({ isActive }) =>
      `${className} ${isActive ? "bg-blue-900 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
    }
  >
    <Inbox className="h-4 w-4" /> <span>Available<span className="hidden sm:inline"> jobs</span></span>
  </NavLink>
);

const MapLink = ({ className }) => (
  <NavLink
    to="/jobs/map"
    aria-label="Map"
    data-testid="locksmith-nav-map"
    className={({ isActive }) =>
      `${className} ${isActive ? "bg-blue-900 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
    }
  >
    <Map className="h-4 w-4" /> <span className="hidden sm:inline">Map</span>
  </NavLink>
);

const LocksLink = ({ className }) => (
  <NavLink
    to="/jobs/mortise-locks"
    aria-label="Mortise locks"
    data-testid="locksmith-nav-mortise-locks"
    className={({ isActive }) =>
      `${className} ${isActive ? "bg-blue-900 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
    }
  >
    <Lock className="h-4 w-4" /> <span className="hidden sm:inline">Locks</span>
  </NavLink>
);

const PreWarrantVisitsLink = ({ className }) => (
  <NavLink
    to="/jobs/pre-warrant-visits"
    data-testid="agent-nav-pre-warrant-visits"
    className={({ isActive }) =>
      `${className} ${isActive ? "bg-blue-900 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
    }
  >
    <ClipboardList className="h-4 w-4" /> <span className="hidden sm:inline">Pre Warrant Visits</span>
  </NavLink>
);

export default function LocksmithLayout() {
  const { signOut, profile } = useAuth();
  const maps = profile?.company?.maps_enabled !== false;
  const openJobs = !!profile?.open_jobs_enabled;
  const isAgent = (profile?.user_types || [profile?.role]).includes("agent") && profile?.company?.enabled_user_types?.includes("agent");
  const isLocksmith = (profile?.user_types?.length ? profile.user_types : [profile?.role]).includes("locksmith");
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-56 md:block" data-testid="locksmith-sidebar">
        <SidebarShell roleLabel="Locksmith">
          <MyJobsLink className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors" />
          <PartnersLink className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors" />
          {openJobs && <AvailableLink className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors" />}
          {isAgent && <PreWarrantVisitsLink className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors" />}
          {maps && <MapLink className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors" />}
          {isLocksmith && <LocksLink className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors" />}
        </SidebarShell>
      </aside>
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-800 bg-slate-900/95 px-2 text-white backdrop-blur-md md:hidden">
        <span className="flex items-center gap-2 font-display font-bold"><img src="/img/logo-icon.png" alt="Warrant Whizz" className="h-7 w-7 object-contain" /><span className="hidden whitespace-nowrap sm:inline">Warrant Whizz</span></span>
        <div className="flex items-center gap-0.5">
          <MyJobsLink className="flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2 text-sm font-medium" />
          <PartnersLink className="flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2 text-sm font-medium" />
          {openJobs && <AvailableLink className="flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2 text-sm font-medium" />}
          {isAgent && <PreWarrantVisitsLink className="flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2 text-sm font-medium" />}
          {maps && <MapLink className="flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium" />}
          {isLocksmith && <LocksLink className="flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium" />}
          <NavLink
            to="/jobs/profile"
            aria-label="Profile"
            data-testid="locksmith-mobile-profile-link"
            className={({ isActive }) => `flex h-11 w-11 items-center justify-center rounded-lg ${isActive ? "bg-blue-900 text-white" : "text-slate-300 hover:bg-slate-800"}`}
          >
            <UserAvatar userId={profile?.id} stamp={profile?.avatar_updated_at} name={profile?.full_name} className="h-7 w-7 bg-transparent text-current" testId="mobile-avatar" />
          </NavLink>
          <button
            onClick={signOut}
            aria-label="Sign out"
            data-testid="locksmith-mobile-signout-button"
            className="flex h-11 w-11 items-center justify-center rounded-lg text-slate-300 hover:bg-slate-800"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>
      <main className="md:pl-56">
        <div className="mx-auto max-w-3xl space-y-4 p-4 pb-24 sm:p-6">
          <PlatformDashboardAlert />
          <CompanyAnnouncement announcement={profile?.company?.announcement} />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
